import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { IAggiudicatario } from '../interfaces/IAggiudicatario';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/api';
import * as Moment from 'moment'
import * as Constants from '../utils/constants'
import { ICommessa } from '../interfaces/ICommessa';
import { ICantiere } from '../interfaces/ICantiere';
import { CommessaService } from '../services/commessa.service';
import { RequestBuilder } from '../classes/request-builder';
import { Loading } from '../utils/loading';
import { finalize } from 'rxjs/operators';
import { AlertUtil } from '../utils/alert-util';
import { IGruppo } from '../interfaces/IGruppo';
import { GruppoService } from '../services/gruppo.service';

@Component({
  selector: 'app-commessa-modal',
  templateUrl: './commessa-modal.component.html',
  styleUrls: ['./commessa-modal.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CommessaModalComponent implements OnInit {

  commessa: ICommessa //commessa selected if update
  isNewCommessa: boolean
  it: any //object for italian calendar
  aggiudicatari: IAggiudicatario[]
  commessaForm: FormGroup
  minDateFineLavori: Date

  listaGruppi: IGruppo[]
  selectedGruppo: IGruppo

  constructor(private formBuilder: FormBuilder, private ref: DynamicDialogRef, private config: DynamicDialogConfig,
    private gruppoService: GruppoService, private commessaService: CommessaService,
    private alertUtil: AlertUtil,
    private cd: ChangeDetectorRef) {
    this.commessa = this.config.data.commessa
    this.aggiudicatari = this.config.data.aggiudicatari

    this.isNewCommessa = !this.commessa //it's new commessa if the commessa selected is undefined

    // build italian options for calendar
    this.it = {
      firstDayOfWeek: 1,
      dayNames: ["domenica", "lunedì", "martedì", "mercoldì", "giovedì", "venerdì", "sabato"],
      dayNamesShort: ["do", "lu", "ma", "me", "gio", "ve", "sa"],
      dayNamesMin: ["D", "L", "m", "M", "G", "V", "S"],
      monthNames: ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"],
      monthNamesShort: ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"],
      today: 'Oggi',
      clear: 'Cancella'
    }
  }

  ngOnInit() {
    this.getAllGruppi()
    this.setForm()
  }

  setForm() {
    //build form group
    this.commessaForm = this.formBuilder.group({
      'codice': [this.isNewCommessa ? '' : this.commessa.Codice, {validators: [Validators.required], updateOn: 'blur'}],
      'nome': [this.isNewCommessa ? '' : this.commessa.Nome, {validators: [Validators.required], updateOn: 'blur'}],
      'descrizione': [this.isNewCommessa ? '' : this.commessa.Descrizione],
      'aggiudicatario': [this.isNewCommessa ? '' : this.commessa.Aggiudicatario, Validators.required],
      'dataInizioLavori': [this.isNewCommessa ? '' : this.commessa.Data_inizio_lavori != null ? new Date(this.commessa.Data_inizio_lavori) : ''],
      'dataFineLavori': [this.isNewCommessa ? '' : this.commessa.Data_fine_lavori != null ? new Date(this.commessa.Data_fine_lavori) : ''],
      'responsabile': [this.isNewCommessa ? '' : this.commessa.Responsabile],
      'cup': [this.isNewCommessa ? '' : this.commessa.CUP],
      'cig': [this.isNewCommessa ? '' : this.commessa.CIG],
      'codiceContratto': [this.isNewCommessa ? '' : this.commessa.Codice_contratto],
      'linkGIS': [this.isNewCommessa ? '' : this.commessa.LinkGIS],
      'idGruppo': [this.isNewCommessa ? '' : this.commessa.Gruppi, Validators.required]
    })
  }

  /**
   * callback fired when commessa form is submitted
   */
  onSubmit() {
    //retrieve values from form
    let codice = this.commessaForm.controls['codice'].value
    let nome = this.commessaForm.controls['nome'].value
    let descrizione = this.commessaForm.controls['descrizione'].value
    let aggiudicatario = this.commessaForm.controls['aggiudicatario'].value
    let dataInizioLavori = this.commessaForm.controls['dataInizioLavori'].value
    let dataFineLavori = this.commessaForm.controls['dataFineLavori'].value
    let responsabile = this.commessaForm.controls['responsabile'].value
    let cup = this.commessaForm.controls['cup'].value
    let cig = this.commessaForm.controls['cig'].value
    let codiceContratto = this.commessaForm.controls['codiceContratto'].value
    let linkGIS = this.commessaForm.controls['linkGIS'].value

    // format dates
    let dataInizioLavoriParsed = dataInizioLavori ? Moment(dataInizioLavori).format("YYYY-MM-DDTHH:mm:ss") : null
    let dataFineLavoriParsed = dataFineLavori ? Moment(dataFineLavori).format("YYYY-MM-DDTHH:mm:ss") : null

    //build cantiere form commessa (set its name equals to commessa one)
    let cantiere: ICantiere = {
      Nome: nome + "_CANTIERE"
    }

    //build commessa
    if (this.isNewCommessa) {
      // create new commessa if this is a new one
      this.commessa = {
        Codice: codice,
        Nome: nome,
        Descrizione: descrizione,
        Data_inizio_lavori: dataInizioLavoriParsed,
        Data_fine_lavori: dataFineLavoriParsed,
        Responsabile: responsabile,
        GUID_autore: localStorage.getItem(Constants.LS_USER_GUID),
        CUP: cup,
        CIG: cig,
        Codice_contratto: codiceContratto,
        Censita: false,
        LinkGIS: linkGIS,
        Cantieri: [cantiere],
        Aggiudicatario: aggiudicatario
      }
    }
    else {
      // update existing fiels if this commessa already exists
      this.commessa.Codice = codice
      this.commessa.Nome = nome
      this.commessa.Descrizione = descrizione
      this.commessa.Aggiudicatario = aggiudicatario
      this.commessa.Data_inizio_lavori = dataInizioLavoriParsed
      this.commessa.Data_fine_lavori = dataFineLavoriParsed
      this.commessa.Responsabile = responsabile
      this.commessa.CUP = cup
      this.commessa.CIG = cig
      this.commessa.Codice_contratto = codiceContratto
      this.commessa.LinkGIS = linkGIS
    }


    // confirm save
    this.alertUtil.showConfirmationAlert("LBL_CONFIRM_SAVE_COMMESSA", null, null, null, null, () => {
      //save commessa is user confirm
      Loading.isLoading = true
      let reqBuilder = new RequestBuilder(this.commessa)
      this.commessaService.save(reqBuilder).pipe(finalize(() => {
        Loading.isLoading = false
      })).subscribe(
        response => {
          if (response.data) {
            this.commessa.Id = response.data.Id
            this.associaCommessaAlGruppo()
          }
          else if (response.error) {
            let errorKey = "LBL_ERROR_" + response.error.Code
            this.alertUtil.showErrorAlert(errorKey)
          }
          else {
            this.alertUtil.showErrorAlert("LBL_SERVER_ERROR_GENERIC", { error: response.Message })
          }
        },
        error => {
          let errorKey = "LBL_HTTP_ERROR_0"
          if (error.status) {
            errorKey = "LBL_HTTP_ERROR_" + error.status
          }
          this.alertUtil.showErrorAlert(errorKey)
        }
      )
    })

  }

  /**
   * callback fired when data inizio lavori changes
   * @param event 
   */
  onInizioLavoriChange(event) {
    let momentInizioLavori = Moment(event)
    this.minDateFineLavori = momentInizioLavori.toDate()
  }

  /**
   * fetch gruppi
   */
  getAllGruppi() {
    Loading.isLoading = true
    let reqBuilder = new RequestBuilder({})
    this.gruppoService.getAllGruppi(reqBuilder).pipe(finalize(() => {
      Loading.isLoading = false
    })).subscribe(
      response => {
        if (response.data) {
          this.listaGruppi = response.data
          this.cd.markForCheck();
        }
        else if (response.error) {
          let errorKey = "LBL_ERROR_" + response.error.Code
          this.alertUtil.showErrorAlert(errorKey)
        }
        else {
          this.alertUtil.showErrorAlert("LBL_SERVER_ERROR_GENERIC", { error: response.Message })
        }
      },
      error => {
        let errorKey = "LBL_HTTP_ERROR_0"
        if (error.status) {
          errorKey = "LBL_HTTP_ERROR_" + error.status
        }
        this.alertUtil.showErrorAlert(errorKey)
      })
  }

  /**
   * chiamata al servizio per associare la commessa ad un gruppo
   */
  associaCommessaAlGruppo() {
    Loading.isLoading = true
    let gruppi: any[] = []
    let gruppiId: any[] = this.commessaForm.controls['idGruppo'].value
    if (gruppiId.length > 0) {
      gruppiId.forEach(gruppo => {
        if (gruppo) {
          gruppi.push(gruppo.Id)
        }
      });
    }
    let data = {
      "idcommessa": this.commessa.Id,
      "gruppi": gruppi
    }
    let reqBuilder = new RequestBuilder(data)
    this.commessaService.associaCommessaAGruppo(reqBuilder).pipe(finalize(() => {
      Loading.isLoading = false
    })).subscribe(
      response => {
        if (response.data) {
          this.alertUtil.showAlert("LBL_NEW_COMMESSA_SUCCESS", null, null, null, () => {
            this.ref.close(this.commessa)
          })
        }
        else if (response.error) {
          let errorKey = "LBL_ERROR_" + response.error.Code
          this.alertUtil.showErrorAlert(errorKey)
        }
        else {
          this.alertUtil.showErrorAlert("LBL_SERVER_ERROR_GENERIC", { error: response.Message })
        }
      },
      error => {
        let errorKey = "LBL_HTTP_ERROR_0"
        if (error.status) {
          errorKey = "LBL_HTTP_ERROR_" + error.status
        }
        this.alertUtil.showErrorAlert(errorKey)
      }
    )
  }
}